// Did a ticket move BACKWARDS?
//
// The board reads progress as a shape — a beast loses a horn at every stage
// it survives — so a ticket that goes back up the workflow has to be
// visible, because it is the opposite of what the shape says. Without a
// mark for it, a piece that regains ground looks identical to one that has
// been quietly sitting still.
//
// Operator's words: "a ticket moves backwards in the workflow, so from
// acceptance testing to testing or review or in progress, or from testing
// to review or in progress or from review to in progress."
//
// THE RULE GENERALISES THAT LIST rather than enumerating it: a move is
// backwards when the target sits EARLIER in the workflow than the source.
// The list above is every engaged-to-engaged pair; the rule also catches a
// move back to To Do, which is the same fact stated more strongly.

/**
 * The workflow, in order.
 *
 * Written out rather than imported from `layout.ts`, because importing it
 * would drag `beast.ts` and then **three.js** into a module that is pure
 * string logic. The agreement with `ENGAGED_STAGES` is enforced by a test
 * instead — see workflow.test.ts. A test that fails when the two drift is
 * worth more than an import that costs a 3D library.
 */
export const WORKFLOW_RANK: readonly string[] = [
  'To Do', 'In Progress', 'Review', 'Testing', 'Acceptance Testing',
];

/** Where a status sits in the workflow, or null if it is not on it.
 *
 *  Deliberately null rather than a number for anything else — Blocked, a
 *  terminal status, a name the vocabulary does not know. A blocked ticket
 *  has not gone backwards, it has STOPPED, and treating that as regression
 *  would flood the board with red for the most ordinary event there is. */
export function workflowRank(status: string | null | undefined): number | null {
  if (!status) return null;
  const i = WORKFLOW_RANK.indexOf(status);
  return i < 0 ? null : i;
}

/**
 * A transition that moved work backwards.
 *
 * Both ends must be on the workflow: a move INTO Blocked is not a
 * regression, and a move into a terminal status is the work finishing.
 * Equal ranks are not a regression either — a self-transition is a
 * re-scoring artifact, not a step back.
 */
export function isRegression(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  const a = workflowRank(from);
  const b = workflowRank(to);
  if (a === null || b === null) return false;
  return b < a;
}
